'use client';
import React from 'react';
import Product from '@models/product';
import ListLineItem from './_ListLineItem';
import { Formik, Form, FormikValues, FormikProps } from 'formik';
import useAddToBasket from '../../hooks/basket/useAddToBasket';
import Button from '@repo/ui/src/components/Button';
import useTranslation from '@components/Translation/useTranslation';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import useProducts from '@hooks/product/useProducts';

interface Props {
  data: Array<Product>;
}

const ShopableList = ({ data }: Props) => {
  const { products } = useProducts(data);
  const { close } = useModal(ModalID.shoppableIngredients);
  const { trigger } = useAddToBasket();
  const { t } = useTranslation();

  const initialValues = React.useMemo(
    () => Object.fromEntries(products?.map((product) => [product?.sku, false]) ?? []),
    [products],
  );

  const handleSubmit = async (values: FormikValues) => {
    const selectedSkus = Object.entries(values)
      .filter(([_, isSelected]) => isSelected)
      .map(([sku]) => sku);

    const productsToAdd = products?.filter(
      (product: Product) => selectedSkus.includes(product.sku) && product.isOrderable,
    );

    if (productsToAdd?.length) {
      await trigger(productsToAdd);
    }
    close();
  };

  if (!data) return null;

  return (
    <div className="tw-pb-12 tw-px-16">
      <div className="tw-flex tw-flex-col tw-gap-4 tw-justify-center">
        <p className="tra-text-web-body-m tw-text-text-500 tw-leading-5 tw-mb-7">
          {t('recipeContent.shoppableRecipeDescription', undefined, {
            default: 'Jumpstart your cook by ordering Traeger.com ingredients for this recipe.',
          })}
        </p>
      </div>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
        {(formikProps: FormikProps<Record<string, boolean>>) => {
          const selectedCount = Object.values(formikProps.values).filter(Boolean).length;
          const orderableProductCount =
            products?.filter((product) => product.isOrderable).length || 0;

          const handleSelectAll = () => {
            const allSelected = selectedCount === orderableProductCount;
            const newValues = { ...formikProps.values };

            products?.forEach((product) => {
              if (product.isOrderable) {
                newValues[product.sku] = !allSelected;
              }
            });

            formikProps.setValues(newValues);
          };

          const selectAllText =
            selectedCount === orderableProductCount
              ? t('recipeContent.deselectAllButton', undefined, {
                  default: 'Deselect All Ingredients',
                })
              : t('recipeContent.selectAllButton', undefined, {
                  default: 'Select All Ingredients',
                });

          return (
            <Form>
              <div className="tw-flex tw-justify-start">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="tw-text-primary-500 hover:tw-text-action-400 tw-font-medium"
                >
                  <p className="tra-text-web-utl-label-s">{selectAllText}</p>
                </button>
              </div>
              <ul className="tw-gap-4 tw-flex tw-flex-col tw-pl-0 tw-pt-4 tw-pb-8">
                {products?.map((product, i) => (
                  <ListLineItem key={i} product={product} disabled={!product.isOrderable} />
                ))}
              </ul>
              <Button disabled={selectedCount === 0} type="submit" full>
                {t('productDetailPageContent.addToCartText', undefined, {
                  default: 'Add to Cart',
                })}{' '}
                {selectedCount > 0 ? `(${selectedCount})` : ''}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ShopableList;
